'use client';
// ═══════════════════════════════════════════════════════════════
// LE CALCULATEUR EMBARQUABLE — l'aimant à liens
//
// Pourquoi il existe (24/08/2026, chantier « backlinks »)
// ─────────────────────────────────────────────────────
// Les backlinks sont le seul facteur SEO que le code ne produisait pas : ils
// se demandent, ils s'achètent (mauvaise idée, Google pénalise) ou ils se
// MÉRITENT. Un calculateur gratuit et embarquable est la troisième voie, et
// c'est la seule qu'un développeur peut construire seul.
//
// Le mécanisme, en une phrase : n'importe quel blog de comptable, guide de
// création d'entreprise ou site RH qui écrit « combien coûte un employé en
// Belgique » veut un calculateur sur sa page. On lui donne deux lignes
// d'iframe ; chaque intégration porte un lien de retour VISIBLE vers
// aureussocial.be. Un lien donné éditorialement, contextuellement pertinent,
// et qui se recopie d'un site à l'autre.
//
// ⚠️ CE QUE CETTE PAGE NE DOIT JAMAIS CONTENIR
// Elle est la SEULE de l'app autorisée à être affichée dans un iframe tiers
// (`frame-ancestors *`). Cette autorisation n'est acceptable QUE parce que la
// page ne touche à rien : aucune session, aucun cookie, aucune donnée client,
// aucun appel Supabase. Elle ne calcule que des constantes légales publiques.
// Verrou : __tests__/embed-calculateur-etanche.test.js — il passe au rouge si
// ce répertoire importe de l'auth, du Supabase ou quoi que ce soit de sensible.
//
// ⚠️ La page est en `noindex` : sinon elle ferait doublon avec /calculateur et
// lui prendrait son classement. Le backlink ne vient PAS d'elle — il vient de
// la page du site hôte qui la cite.
// ═══════════════════════════════════════════════════════════════
import { useState } from 'react';
import { calculer } from '../../calculateur/_calc';

const OR = '#c6a34e';

const TXT = {
  fr: {
    titre: 'Salaire brut → net',
    brut: 'Salaire brut mensuel',
    statut: 'Statut',
    employe: 'Employé',
    ouvrier: 'Ouvrier',
    net: 'Net estimé',
    cout: 'Coût employeur',
    onss: 'ONSS travailleur',
    pp: 'Précompte professionnel',
    csss: 'Cotisation spéciale',
    bonus: 'Bonus à l’emploi',
    fonds: 'Fonds sectoriel',
    pension: 'Pension sectorielle (2e pilier)',
    par: 'Calcul par',
    note: 'Estimation isolé, sans enfant à charge. Le net réel dépend de votre commission paritaire et de votre situation.',
  },
  nl: {
    titre: 'Brutoloon → nettoloon',
    brut: 'Bruto maandloon',
    statut: 'Statuut',
    employe: 'Bediende',
    ouvrier: 'Arbeider',
    net: 'Geschat netto',
    cout: 'Werkgeverskost',
    onss: 'RSZ werknemer',
    pp: 'Bedrijfsvoorheffing',
    csss: 'Bijzondere bijdrage',
    bonus: 'Werkbonus',
    fonds: 'Sectorfonds',
    pension: 'Sectoraal pensioen (2e pijler)',
    par: 'Berekening door',
    note: 'Raming alleenstaande, zonder kinderen ten laste. Het werkelijke netto hangt af van uw paritair comité en uw situatie.',
  },
};

const eur = (n, lang) =>
  n == null ? '—' : `${Number(n).toLocaleString(lang === 'nl' ? 'nl-BE' : 'fr-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

export default function Widget({ lang = 'fr', cp = null, brutInitial = 3000 }) {
  const t = TXT[lang] || TXT.fr;
  const [brut, setBrut] = useState(brutInitial);
  const [statut, setStatut] = useState('employe');
  const r = calculer(brut, { statut, cp });

  // Le lien de retour est la RAISON D'ÊTRE de ce widget. Il n'est ni caché, ni
  // en nofollow, ni en petit gris illisible : un intégrateur doit le voir et
  // l'accepter, sinon l'intégration est déloyale et ne tiendra pas.
  const lienRetour = lang === 'nl'
    ? 'https://www.aureussocial.be/berekening'
    : 'https://www.aureussocial.be/calculateur';

  const L = { display: 'block', fontSize: 12, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: '#8a8f98', marginBottom: 6 };
  const I = { width: '100%', padding: '10px 12px', fontSize: 16, borderRadius: 8, border: '1px solid #d8dbe0', background: '#fff', color: '#1a1a18', boxSizing: 'border-box' };
  const ligne = (k, v, fort) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #edeef0', fontWeight: fort ? 800 : 400, fontSize: fort ? 17 : 14 }}>
      <span style={{ color: fort ? '#1a1a18' : '#5b6068' }}>{k}</span>
      <span style={{ color: fort ? OR : '#1a1a18', fontVariantNumeric: 'tabular-nums' }}>{v}</span>
    </div>
  );

  return (
    <div style={{ fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif', color: '#1a1a18', background: '#fff', padding: 18, maxWidth: 520, margin: '0 auto', boxSizing: 'border-box' }}>
      <h1 style={{ fontSize: 19, fontWeight: 800, margin: '0 0 14px' }}>
        {t.titre}{cp ? ` — CP ${cp}` : ''}
      </h1>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 190px' }}>
          <label style={L} htmlFor="ae-brut">{t.brut}</label>
          <input
            id="ae-brut" type="number" min="0" step="50" value={brut} style={I}
            onChange={(e) => setBrut(Math.max(0, Number(e.target.value) || 0))}
          />
        </div>
        <div style={{ flex: '1 1 130px' }}>
          <label style={L} htmlFor="ae-statut">{t.statut}</label>
          <select id="ae-statut" value={statut} style={I} onChange={(e) => setStatut(e.target.value)}>
            <option value="employe">{t.employe}</option>
            <option value="ouvrier">{t.ouvrier}</option>
          </select>
        </div>
      </div>

      {r && (
        <div style={{ marginBottom: 14 }}>
          {ligne(t.onss, `− ${eur(r.onssTravailleur, lang)}`)}
          {r.bonusEmploi > 0 && ligne(t.bonus, `+ ${eur(r.bonusEmploi, lang)}`)}
          {ligne(t.pp, `− ${eur(r.precompte, lang)}`)}
          {r.csss > 0 && ligne(t.csss, `− ${eur(r.csss, lang)}`)}
          {ligne(t.net, eur(r.net, lang), true)}
          {ligne(t.cout, eur(r.coutEmployeur, lang))}
          {r.fondsSectoriel > 0 && ligne(`${t.fonds}${r.fondsSectorielNom ? ` — ${r.fondsSectorielNom}` : ''}`, `+ ${eur(r.fondsSectoriel, lang)}`)}
          {r.pensionSectorielle > 0 && ligne(t.pension, `+ ${eur(r.pensionSectorielle, lang)}`)}
        </div>
      )}

      <p style={{ fontSize: 11, color: '#8a8f98', lineHeight: 1.5, margin: '0 0 12px' }}>{t.note}</p>

      <p style={{ margin: 0, fontSize: 12, borderTop: '1px solid #edeef0', paddingTop: 10 }}>
        <span style={{ color: '#8a8f98' }}>{t.par} </span>
        <a href={lienRetour} target="_blank" rel="noopener" style={{ color: OR, fontWeight: 700, textDecoration: 'none' }}>
          Aureus Social&nbsp;Pro
        </a>
      </p>
    </div>
  );
}
